import {
    ChevronUp,
    Cloud,
    User,
    TrendingDown,
    TrendingUp,
    ShieldCheck,
    Users as UsersIcon
} from "lucide-react";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { NGROK } from "../../../APIs";
import { SiAmazon } from "react-icons/si";
import { CloudUsersPageProps } from "./interface";
import {
    getAWSUsersList,
    getAzureUsersList,
    getGCPUsersList,
} from "./Data";


import { Card, CardContent } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { DetailsDialog } from "./DetailsDialog";
import { Link } from "react-router-dom";
import { myLocalStorage } from "../../StorageHelper";
import { VscAzure } from "react-icons/vsc";
import { FaGoogle } from "react-icons/fa6";
import TenantCloudResources from "../TenantCloud/TenantCloudResources";
import useUserStore from "../../../services/userStore";
import useNotificationsStore from "../../../services/notificationStore";
import { triggerCloudSync } from "../../../triggerCloudSync";
import useCloudSyncStore from "../../../services/useCloudSyncStore";
import useCloudSyncWatcher from "../../../services/useCloudSyncWatcher";
import CloudFilters from "../CloudFilters";
import AlertTrendPanel from "./AlertTrendPanel";
import { RiskTagBadge, TotalAlertsBadge } from "./RiskTagBadge";
import { SecurityProgressSkeleton, SummaryCardsSkeleton } from "./CloudDashboardSkeletons";
import EmptyStateCard from "../EmptyStateCard";
import CardPagination from "../CardPagination";
import { getCloudAccountSelection } from "../cloudAccountSelection";
// Ignore missing type declarations for CSS side-effect import
// @ts-ignore
import "./CloudStatus.css";
import "./CloudMetadata.css";

const PAGE_SIZE = 10;

const getActivityColor = (count: number) => {
    if (count < 10) return "bg-gradient-to-r from-yellow-400 to-yellow-500";
    if (count <= 50) return "bg-gradient-to-r from-orange-400 to-orange-500";
    return "bg-gradient-to-r from-orange-600 to-orange-700";
};

const getActivityIntensity = (count: number) => {
    return Math.min(Math.max(Number(count) || 0, 0), 100);
};

function ActivityBar({ count, className = "" }: { count: number; className?: string }) {
    const intensity = getActivityIntensity(count);
    const colorClass = getActivityColor(count);

    return (
        <div className={`relative h-2 bg-muted rounded-full overflow-hidden ${className}`}>
            <div
                className={`h-full ${colorClass} rounded-full transition-all`}
                style={{ width: `${intensity}%` }}
            />
        </div>
    );
}

const CloudIcon = ({ provider }: { provider: string }) => {
    if (provider === "gcp") return <FaGoogle className="w-8 h-8 text-primary" />;
    if (provider === "aws") return <SiAmazon className="w-8 h-8 text-primary" />;
    return <VscAzure className="w-8 h-8 text-primary" />;
};

const filterRiskTags = (tags: string[]): string[] => {
    return tags.filter(t => !/^[+-]?\d+\s*alerts?$/i.test(t.trim()));
};


export default function Users({ cloudProvider }: CloudUsersPageProps) {
    const [selectedTenant, setSelectedTenant] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
    const [cloudProviderChanges, setCloudProviderChanges] = useState<any>();
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [securityProgress, setSecurityProgress] = useState<any>(null);
    const [securityProgressLoading, setSecurityProgressLoading] = useState(true);
    // Show the dashboard skeleton only on the first load; background refreshes update in place
    // so the panels above the list don't flip to skeleton and shift the list under the cursor.
    const securityProgressLoadedRef = useRef(false);
    const [hasMultipleSnapshots, setHasMultipleSnapshots] = useState(false);
    const { role } = useUserStore((state) => state.user);
    const { userAWSApprovedNotifications } = useNotificationsStore();
    const awsAccessRevision = useMemo(() => cloudProvider === "aws"
        ? userAWSApprovedNotifications
            .map((notification: any) => `${notification.id}:${notification.requestStatus}:${notification.isPolicyAdded}:${notification.generatedPoliciesARN || ""}`)
            .sort()
            .join("|")
        : "", [cloudProvider, userAWSApprovedNotifications]);
    const { syncStatus } = useCloudSyncStore((state) => ({
        syncStatus: state.syncStatus[cloudProvider],
    }));
    const accountSelection = getCloudAccountSelection(cloudProvider);
    const hasCloudAccount = accountSelection.configured;

    const fetchSyncStatus = useCallback(async () => {
        try {
            const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || myLocalStorage.getItem("latestTenant");
            if (!tenantName) return;
            const selection = getCloudAccountSelection(cloudProvider);
            if (!selection.configured) {
                setHasMultipleSnapshots(false);
                return;
            }
            const { data } = await axios.get(`${NGROK}/api/cloud/dashboard/sync-status`, {
                params: { tenantName, cloudProviderType: cloudProvider.toUpperCase() },
            });
            if (!Array.isArray(data) || data.length === 0) {
                setHasMultipleSnapshots(false);
                return;
            }
            const entry = selection.id
                ? data.find((s: any) => s.cloudAccountId === selection.id)
                : data[0];
            const prev = entry?.previousSnapshotVersionId;
            setHasMultipleSnapshots(Array.isArray(prev) && prev.length > 0);
        } catch (err) {
            console.error("Error fetching sync status:", err);
        }
    }, [cloudProvider]);

    const riskyLabel = hasMultipleSnapshots ? "Risk Increased" : "Risky";

    const fetchSecurityProgress = useCallback(async () => {
        if (!securityProgressLoadedRef.current) setSecurityProgressLoading(true);
        try {
            const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || myLocalStorage.getItem("latestTenant");
            if (!tenantName) return;
            const selection = getCloudAccountSelection(cloudProvider);
            if (!selection.configured) {
                setSecurityProgress(null);
                setSecurityProgressLoading(false);
                securityProgressLoadedRef.current = true;
                return;
            }
            const cp = cloudProvider?.toLowerCase();
            let url = "";
            let params: any = { tenantName };
            if (cp === "gcp") {
                url = `${NGROK}/api/gcp/security-progress`;
                params.projectId = selection.id;
            } else if (cp === "aws") {
                url = `${NGROK}/api/aws/security-progress`;
                params.accountId = selection.id;
            } else if (cp === "azure") {
                url = `${NGROK}/api/azure/v1/security-progress`;
                params.subscriptionId = selection.id;
            }
            if (!url) return;
            const { data } = await axios.get(url, { params });
            // Store the users-specific sub-object for the Users page
            setSecurityProgress(data?.overallSecurityProgress?.users || null);
        } catch (err) {
            console.error("Error loading security progress:", err);
            setSecurityProgress(null);
        } finally {
            setSecurityProgressLoading(false);
            securityProgressLoadedRef.current = true;
        }
    }, [cloudProvider]);

    const refetchUsers = useCallback(async (options?: { live?: boolean }) => {
        if (!role || role === "TENANT_USER") return;

        if (!getCloudAccountSelection(cloudProvider).configured) {
            setUsers([]);
            setSecurityProgress(null);
            setSecurityProgressLoading(false);
            securityProgressLoadedRef.current = true;
            if (!options?.live) setLoading(false);
            return;
        }
        const isLiveRefresh = options?.live === true;
        if (!isLiveRefresh) {
            setLoading(true);
        }
        try {
            let data: any[] = [];
            if (cloudProvider === "gcp") {
                data = await getGCPUsersList();
            } else if (cloudProvider === "aws") {
                data = await getAWSUsersList();
            } else if (cloudProvider === "azure") {
                data = await getAzureUsersList();
            }
            setUsers(Array.isArray(data) ? data : []);
            await fetchSecurityProgress();
        } catch (err) {
            console.error("Error loading users:", err);
            if (!isLiveRefresh) {
                setUsers([]);
            }
        } finally {
            if (!isLiveRefresh) {
                setLoading(false);
            }
        }
    }, [cloudProvider, fetchSecurityProgress, role]);

    useCloudSyncWatcher(cloudProvider, selectedTenant, refetchUsers);

    useEffect(() => {
        if (cloudProvider === "aws" && hasCloudAccount) {
            refetchUsers({ live: true });
        }
    }, [cloudProvider, hasCloudAccount, awsAccessRevision, refetchUsers]);

    useEffect(() => {
        if (!role || role === "TENANT_USER") return;

        if (!hasCloudAccount) {
            setUsers([]);
            setLoading(false);
            setSecurityProgress(null);
            setSecurityProgressLoading(false);
            securityProgressLoadedRef.current = true;
            setHasMultipleSnapshots(false);
            return;
        }
        if (!cloudProviderChanges) return;

        const fetchUsers = async () => {
            setLoading(true);
            try {
                let data: any[] = [];
                if (cloudProvider === "gcp") {
                    data = await getGCPUsersList();
                } else if (cloudProvider === "aws") {
                    data = await getAWSUsersList();
                } else if (cloudProvider === "azure") {
                    data = await getAzureUsersList();
                }
                setUsers(data);
            } catch (err) {
                console.error("Error loading users:", err);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
        fetchSecurityProgress();
        fetchSyncStatus();
    }, [cloudProvider, selectedTenant, cloudProviderChanges, hasCloudAccount, fetchSecurityProgress, fetchSyncStatus, role]);

    const toggleRoles = (userId: string) => {
        const newExpanded = new Set(expandedRoles);
        if (newExpanded.has(userId)) newExpanded.delete(userId);
        else newExpanded.add(userId);
        setExpandedRoles(newExpanded);
    };

    const handleSync = async () => {
        if (!hasCloudAccount) return;
        try {
            let syncResult: any;
            if (cloudProvider === "aws") {
                syncResult = await triggerCloudSync(
                    "aws",
                    "/api/aws/ondemand",
                    selectedTenant,
                    refetchUsers
                );
            } else if (cloudProvider === "azure") {
                syncResult = await triggerCloudSync(
                    "azure",
                    "/api/azure-sync/onDemand",
                    selectedTenant,
                    refetchUsers
                );
            } else if (cloudProvider === "gcp") {
                syncResult = await triggerCloudSync(
                    "gcp",
                    "/api/gcp-sync/manual",
                    selectedTenant,
                    refetchUsers
                );
            }

            if (syncResult?.alreadyRunning) {
                console.info(syncResult.message);
            } else if (syncResult?.timedOut || syncResult?.started) {
                console.info(syncResult.message || "Sync started in background");
            }
        } catch (err) {
            console.error("Error syncing users:", err);
        }
    };

    const handleTenantChange = (tenant: string) => {
        setSelectedTenant(tenant);
    };
    const handleCloudProvider = (value: any) => {
        setCloudProviderChanges(value);
    };
    const [riskFilter, setRiskFilter] = useState<"all" | "improved" | "worsened" | "neutral">("all");
    // Reset the page to the complete entity list whenever the cloud/tenant context changes.
    useEffect(() => {
        setRiskFilter("all");
        securityProgressLoadedRef.current = false;
    }, [cloudProvider, selectedTenant]);

    /** Map API riskChangeCategory to local status */
    const mapRiskCategory = (cat: string | null | undefined): "improved" | "worsened" | "neutral" => {
        switch (cat) {
            case "RISK_INCREASED": return "worsened";
            case "RISK_REDUCED": return "improved";
            case "NEUTRAL": return "neutral";
            default: return "neutral";
        }
    };

    const activeEntities = useMemo(() => {
        return users.map(u => ({
            key: u.email || u.id || u.uniqueId || Math.random().toString(),
            name: u.name || u.userName || (u.email ? u.email.split("@")[0] : "Unknown"),
            subtitle: u.email || "",
            roles: u.roles || [],
            activity: u.activity || 0,
            type: "user" as const,
            raw: u
        }));
    }, [users]);

    const analyzedEntities = useMemo(() => activeEntities.map(a => {
        const u = a.raw;
        const status = mapRiskCategory(u.riskChangeCategory);
        return {
            entity: a,
            status,
            alertCount: u.alertCount || 0,
            alertDelta: u.alertCountDelta || 0,
            highPrivilegeRoleCount: u.highPrivilegeRoleCount || 0,
            highPrivilegeRoleCountDelta: u.highPrivilegeRoleCountDelta || 0,
            riskTags: u.riskTags || [],
            tagInfo: u.tagInfo || {},
            highPrivAdded: u.highPrivilegeRoleCountDelta > 0 ? Array(u.highPrivilegeRoleCountDelta).fill("high-priv") : [],
            highPrivRemoved: u.highPrivilegeRoleCountDelta < 0 ? Array(Math.abs(u.highPrivilegeRoleCountDelta)).fill("high-priv") : [],
        } as any;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [activeEntities]);

    const improved = useMemo(() => analyzedEntities.filter(a => a.status === "improved"), [analyzedEntities]);
    const worsened = useMemo(() => analyzedEntities.filter(a => a.status === "worsened"), [analyzedEntities]);
    const neutral = useMemo(() => analyzedEntities.filter(a => a.status === "neutral"), [analyzedEntities]);

    // Use security-progress API users sub-object for the overall bar + hero cards
    const sp = securityProgress;
    const displayTotal = sp?.totalEntities ?? users.length;
    const displayImproved = sp?.riskReducedCount ?? improved.length;
    const displayWorsened = sp?.riskIncreasedCount ?? worsened.length;
    const displayNeutral = sp?.neutralCount ?? neutral.length;

    const improvePct = sp?.greenBarPercentage ?? (displayTotal ? (displayImproved / displayTotal) * 100 : 0);
    const worsenPct = sp?.redBarPercentage ?? (displayTotal ? (displayWorsened / displayTotal) * 100 : 0);
    const neutralPct = displayTotal ? Math.max(100 - improvePct - worsenPct, 0) : 0;
    const dashboardLoading = hasCloudAccount && (loading || securityProgressLoading || !cloudProviderChanges || (syncStatus && users.length === 0));

    const filteredAnalyzed = useMemo(() => {
        let list = analyzedEntities;
        if (riskFilter !== "all") {
            list = list.filter(a => a.status === riskFilter);
        }
        if (searchQuery) {
            const sq = searchQuery.toLowerCase();
            list = list.filter(a =>
                a.entity.name.toLowerCase().includes(sq) ||
                a.entity.subtitle.toLowerCase().includes(sq)
            );
        }
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyzedEntities, riskFilter, searchQuery]);

    const filteredUsers = useMemo(
        () => {
            if (!Array.isArray(users)) return [];
            return filteredAnalyzed;
        },
        [users, filteredAnalyzed]
    );

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pagedUsers = filteredUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    useEffect(() => {
        setPage(1);
    }, [cloudProvider, selectedTenant, accountSelection.id, searchQuery, riskFilter]);

    useEffect(() => {
        setPage((current) => Math.min(current, totalPages));
    }, [totalPages]);




    const getUserLink = (user: any) => {
        if (cloudProvider === "aws") {
            user["userPolicies"] = user.roles.map((role: any) => ({
                policyName: role
            }));
            user["username"] = user.name;
            user["userId"] = user.uniqueId;
            user["createDate"] = user.syncedAt;
            return {
                pathname: "/aws/users/userDetails",
                state: {
                    user,
                    groups: user.groups,
                    isconfigured: user.isAWSUserConfigured,
                    selectedAwsAccount: "",
                },
            };
        }

        if (cloudProvider === "azure") {
            console.log(user)
            user["azureUserId"] = user.uniqueId;
            user["displayName"] = user.name;
            user["userPrincipalName"] = user.email || user.principalName;
            return {
                pathname: "/azure-directory/azureusers/userDetails",
                state: {
                    user,
                    groups: user.groups,
                    roles: user.roles,
                    isconfigured: false,
                },
            };
        }

        if (cloudProvider === "gcp") {
            user["userName"] = user.name;
            user["projectId"] = myLocalStorage.getItem("gcpSelectedProjectId");
            return {
                pathname: "/gcp/gcpusers/userDetails",
                state: {
                    user,
                    groups: user.groups,
                    roles: user.roles,
                    isconfigured: false,
                },
            };
        }
        return { pathname: "#", state: {} };
    };

    if (role === "TENANT_USER") {
        return <TenantCloudResources cloudProvider={cloudProvider} />
    }

    if (!hasCloudAccount) {
        return (
            <div className="h-full flex flex-col bg-background">
                <div className="p-6">
                    <CloudFilters
                        cloudProvider={cloudProvider}
                        onTenantChange={handleTenantChange}
                        onCloudAccountChange={handleCloudProvider}
                        isAdmin={role !== "TENANT_USER"}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        searchPlaceholder="Search users…"
                        onSync={handleSync}
                        syncDisabled
                        syncLoading={false}
                    />
                    <EmptyStateCard
                        icon={Cloud}
                        title={accountSelection.title}
                        description={accountSelection.description}
                    />
                </div>
            </div>
        );
    }


    return (
        <div className="h-full flex flex-col bg-background">
            <div className="p-6 " >
                {/* Cloud Filters */}
                <CloudFilters
                    cloudProvider={cloudProvider}
                    onTenantChange={handleTenantChange}
                    onCloudAccountChange={handleCloudProvider}
                    isAdmin={role !== "TENANT_USER"}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    searchPlaceholder="Search users…"
                    onSync={handleSync}
                    syncDisabled={syncStatus}
                    syncLoading={syncStatus}
                />

                {/**syncStatus && (
                    <div className="mb-4 flex items-center gap-3 rounded-xl border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm text-foreground">
                        <RefreshCw className="h-4 w-4 animate-spin text-sky-500 flex-shrink-0" />
                        <span>Cloud sync in progress. Data will refresh automatically when complete.</span>
                    </div>
                )**}

                {/* Users List */}
                <div className="mb-6 space-y-6">
                    {dashboardLoading ? (
                        <SecurityProgressSkeleton riskyLabel={riskyLabel} />
                    ) : (
                        <div className="cs-progress-hero" style={{ margin: 0 }}>
                            <div className="cs-progress-details">
                                <div className="cs-progress-title-row">
                                    <h3 className="cs-progress-main-title">Overall Security Progress</h3>
                                    <span className="cs-progress-subtitle">{displayTotal} entities analyzed</span>
                                </div>

                                <div className="cs-bar-wrap">
                                    <div className="cs-bar-track">
                                        <div className="cs-bar-fill improved" style={{ width: `${improvePct}%` }} />
                                        <div className="cs-bar-fill worsened" style={{ width: `${worsenPct}%` }} />
                                        <div className="cs-bar-fill unchanged" style={{ width: `${neutralPct}%` }} />
                                    </div>
                                </div>

                                <div className="cs-bar-legend">
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot improved" />
                                        <TrendingDown className="cs-bar-legend-icon improved" />
                                        <span className="cs-bar-legend-count">{displayImproved}</span>
                                        <span className="cs-bar-legend-label">Risk Reduced</span>
                                    </div>
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot worsened" />
                                        <TrendingUp className="cs-bar-legend-icon worsened" />
                                        <span className="cs-bar-legend-count">{displayWorsened}</span>
                                        <span className="cs-bar-legend-label">{riskyLabel}</span>
                                    </div>
                                    <div className="cs-bar-legend-item">
                                        <div className="cs-bar-legend-dot unchanged" />
                                        <ShieldCheck className="cs-bar-legend-icon unchanged" />
                                        <span className="cs-bar-legend-count">{displayNeutral}</span>
                                        <span className="cs-bar-legend-label">Neutral</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <AlertTrendPanel cloudProvider={cloudProvider} scope="USER" />

                    {dashboardLoading ? (
                        <SummaryCardsSkeleton
                            className="!mt-4"
                            cards={[
                                { label: "Total Users", icon: <UsersIcon />, colorClass: "blue" },
                                { label: riskyLabel, icon: <TrendingUp />, colorClass: "red" },
                                { label: "Risk Reduced", icon: <TrendingDown />, colorClass: "green" },
                                { label: "Neutral", icon: <ShieldCheck />, colorClass: "gray" },
                            ]}
                        />
                    ) : (
                        <div className="cs-hero" style={{ margin: 0, marginTop: "16px" }}>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon blue"><UsersIcon /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayTotal}</span>
                                    <span className="cs-hero-label">Total Users</span>
                                </div>
                            </div>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon red"><TrendingUp /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayWorsened}</span>
                                    <span className="cs-hero-label">{riskyLabel}</span>
                                </div>
                            </div>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon green"><TrendingDown /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayImproved}</span>
                                    <span className="cs-hero-label">Risk Reduced</span>
                                </div>
                            </div>
                            <div className="cs-hero-card">
                                <div className="cs-hero-icon gray"><ShieldCheck /></div>
                                <div className="cs-hero-info">
                                    <span className="cs-hero-value">{displayNeutral}</span>
                                    <span className="cs-hero-label">Neutral</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Status Filters */}
                    <Card className="cloud-card-surface cloud-list-controls rounded-2xl flex flex-wrap items-center justify-between gap-3 p-3">
                        <div className="cs-tabs !flex-wrap">
                            <button className={`cs-tab all-tab ${riskFilter === "all" ? "active" : ""}`} onClick={() => setRiskFilter("all")}>
                                <ShieldCheck /> All <span className="cs-tab-count">{dashboardLoading ? "..." : analyzedEntities.length}</span>
                            </button>
                            <button className={`cs-tab worsened-tab ${riskFilter === "worsened" ? "active" : ""}`} onClick={() => setRiskFilter("worsened")}>
                                <TrendingUp /> {riskyLabel} <span className="cs-tab-count">{dashboardLoading ? "..." : worsened.length}</span>
                            </button>
                            <button className={`cs-tab improved-tab ${riskFilter === "improved" ? "active" : ""}`} onClick={() => setRiskFilter("improved")}>
                                <TrendingDown /> Risk Reduced <span className="cs-tab-count">{dashboardLoading ? "..." : improved.length}</span>
                            </button>
                            <button className={`cs-tab unchanged-tab ${riskFilter === "neutral" ? "active" : ""}`} onClick={() => setRiskFilter("neutral")}>
                                <ShieldCheck /> Neutral <span className="cs-tab-count">{dashboardLoading ? "..." : neutral.length}</span>
                            </button>
                        </div>
                        <CardPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setPage}
                            className="ml-auto"
                            label="Cloud users pagination"
                        />
                    </Card>
                </div>

                {(hasCloudAccount && (loading || syncStatus || !cloudProviderChanges)) && filteredUsers.length === 0 ? (
                    <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Card key={i} className="cloud-card-surface animate-pulse rounded-2xl">
                                <CardContent className="p-4 flex items-center gap-4">
                                    <div className="w-8 h-8 rounded-full bg-muted/60 flex-shrink-0" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-muted/60 rounded w-1/4" />
                                        <div className="h-3 bg-muted/60 rounded w-1/2" />
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <Card className="cloud-card-surface border-dashed rounded-2xl">
                        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                                <CloudIcon provider={cloudProvider} />
                            </div>
                            <h3 className="text-lg font-semibold mb-2">No {cloudProvider.toUpperCase()} users found</h3>
                            <p className="text-sm text-muted-foreground max-w-md mb-6">
                                You currently don't have any user configure for this account.
                            </p>
                            {/**
                                      * <Button variant="outline" onClick={() => navigate("/aws/users")} data-testid="button-go-to-resources">
                                       Go to Resources
                                     </Button>
                                      */}

                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-2">
                        {pagedUsers.map((item) => {
                            const user = item.entity.raw;
                            user.roles = user.roles || [];
                            const analyzed = item;
                            const userLink = getUserLink(user);

                            const isExpanded = expandedRoles.has(user.id || user.email);
                            const visibleRoles = isExpanded ? user.roles : user.roles.slice(0, 2);
                            const hasMore = user.roles.length > 2;
                            return (

                                <Card key={item.entity.key} className="cloud-list-card cursor-pointer rounded-2xl">
                                    <CardContent className="p-4">
                                        <Link
                                            to={userLink.pathname}
                                            state={userLink.state}
                                            className="block hover:no-underline"
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                                    <User className="w-4 h-4 text-primary" />
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <h3 className="font-semibold text-sm text-foreground">
                                                            {user.name}
                                                        </h3>
                                                        <Badge
                                                            variant={user.isDormant ? "secondary" : "default"}
                                                            className={`text-xs h-5 ${user.isDormant
                                                                ? "bg-red-500 text-white border-red-500 dark:bg-red-600 dark:text-white dark:border-red-600"
                                                                : "bg-primary text-primary-foreground border-primary dark:bg-primary dark:text-primary-foreground dark:border-primary"
                                                                }`}
                                                        >
                                                            {user.isDormant ? "Dormant" : "Active"}
                                                        </Badge>
                                                        <TotalAlertsBadge count={analyzed?.alertCount} />
                                                        {analyzed && analyzed.riskTags?.length > 0 && (() => {
                                                            const tags = filterRiskTags(analyzed.riskTags);
                                                            return tags.length > 0 ? (
                                                                <div className="cs-card-tags ml-2 flex flex-wrap gap-1">
                                                                    {tags.map((tag: string, idx: number) => (
                                                                        <RiskTagBadge key={`${tag}-${idx}`} tag={tag} tagInfo={analyzed.tagInfo} />
                                                                    ))}
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                    {cloudProvider === "azure" ? (
                                                        <div className="flex flex-col gap-1.5 mt-2 mb-1.5">
                                                            <div className="flex items-start gap-1.5">
                                                                <span className="text-[11px] font-medium text-muted-foreground w-12 shrink-0 mt-0.5">Email:</span>
                                                                <span className="px-2 py-0.5 text-[11px] cloud-metadata-tag text-muted-foreground rounded-md lowercase font-normal break-all">
                                                                    {user.email}
                                                                </span>
                                                            </div>
                                                            {user.userDetails?.azureId && (
                                                                <div className="flex items-start gap-1.5">
                                                                    <span className="text-[11px] font-medium text-muted-foreground w-12 shrink-0 mt-0.5">Azure Id:</span>
                                                                    <span className="px-2 py-0.5 text-[11px] cloud-metadata-tag text-muted-foreground rounded-md font-mono lowercase font-normal break-all">
                                                                        {user.userDetails.azureId}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="text-[13px] text-muted-foreground truncate mt-1">
                                                            {user.email}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                                    <div className="text-xs text-muted-foreground">Activity</div>
                                                    <ActivityBar count={user.activity} className="w-20" />
                                                </div>
                                            </div>
                                        </Link>
                                        <div className="mt-3 pt-3 border-t flex items-start gap-6">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <div className="text-[11px] font-medium text-muted-foreground mr-1">Roles:</div>
                                                    {visibleRoles.map((role: any, idx: any) => (
                                                        <Badge
                                                            key={idx}
                                                            variant={role === "Owner" ? "default" : "secondary"}
                                                            className={`text-xs h-5 ${role === "Owner" ? "" : "cloud-metadata-tag"}`}
                                                        >
                                                            {role}
                                                        </Badge>
                                                    ))}

                                                    {hasMore && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault(); // prevent navigation on toggle
                                                                toggleRoles(user.id || user.email);
                                                            }}
                                                            className="text-xs text-primary hover:underline font-medium flex items-center gap-0.5"
                                                        >
                                                            {isExpanded ? (
                                                                <>
                                                                    <ChevronUp className="w-3 h-3" />
                                                                    Less
                                                                </>
                                                            ) : (
                                                                <>+{user.roles.length - 2}</>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex-shrink-0 text-right">
                                                <div className="text-xs text-muted-foreground mb-1.5">
                                                    Last Activity
                                                </div>
                                                <div className="text-xs font-medium">
                                                    {user.lastActivityMessage}
                                                </div>
                                            </div>
                                        </div>


                                        <div className="mt-2 flex items-center justify-between">
                                            <div className="flex items-center gap-1.5">
                                                <div className="text-[11px] font-medium text-muted-foreground mr-1">Synced :</div>
                                                <Badge variant="secondary" className="text-[10px] h-5 cloud-metadata-tag">{user.syncedAt}</Badge>
                                            </div>
                                            <DetailsDialog data={user} title={`User: ${user.name}`} />
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}

                    </div>
                )}

            </div>
        </div>
    );
}
